import { useEffect, useRef, useState } from 'react'

import Axios from '../networking/intraceptor'
import * as path from '../networking/urlEndPoints'
import { authToken } from '../storage/authToken'

/**
 * Maps each top-nav workspace tab (by its `menuItems` name) to its module key in
 * the new module-permission system.
 *
 * With sub-modules these keys are PARENT keys: nobody is granted `dispense`
 * directly any more — the backend derives it from the sub-modules the user
 * holds and returns it in `parents`. TEAM keeps its admin_module gate; the
 * module check is an AND on top of it.
 */
export const WORKSPACE_TAB_MODULE_KEYS = {
    'PATIENTS': 'patients',
    'FORMS': 'forms',
    'COMPOUND': 'compound',
    'DISPENSE': 'dispense',
    'BLISTERS': 'blisters',
    'VIALS': 'vials',
    'SPECIALITY MEDICATION': 'speciality_medication',
    'INVOICE': 'invoice',
    'ARV': 'arv',
    'REVENUE': 'revenue',
    'FDA FORM': 'fda_form',
    'INVENTORY': 'inventory',
    'ACCOUNTABILITY': 'accountability',
    'TRAVEL': 'travel',
    'VACCINE': 'vaccine',
    'PACKAGING': 'packaging',
    // The nav's second packaging entry. It has no `userPermissions` row in
    // workspaceTabs, so it does not render today — but without a key here
    // tabAllowed() would answer "outside the module contract → always true" the
    // moment it ever did. Both entries live under the one `packaging` module.
    'BLISTER PACKAGING': 'packaging',
    'APP': 'app',
    'TEAM': 'team',
}

/**
 * Asks the backend which modules the logged-in user may see (my-modules), so the
 * nav and routes stop trusting the hand-duplicated legacy permission lists.
 *
 * FAIL-OPEN CONTRACT (deploy-skew safety): on ANY failure — an old backend that
 * 404s the endpoint, a 500, a network error, a response without a `modules`
 * array, or no auth token yet (pre-login, when the request is not sent at all)
 * — `modules` is null, and every consumer must treat null as "legacy
 * mode: behave exactly as today". The module system can only ever RESTRICT once
 * the new backend answers; it can never break the portal when it does not.
 *
 * SHAPE:
 *   modules — every key the user holds: the LEAF (sub-module) keys plus the
 *             derived parent keys, so a top-level check keeps working.
 *   parents — the derived top-nav keys on their own. A backend from before
 *             sub-modules does not send it; it is then null and callers fall
 *             back to the `modules` check, which is exactly today's behaviour.
 */
const LEGACY_STATE = { modules: null, parents: null, isAdmin: false, mode: null, emptyGate: false, loading: false }

/**
 * One request per logged-in session, shared by every consumer. The nav, the
 * sidebar and the route table all ask the same question; without this each of
 * them would fire its own identical GET on mount.
 *
 * The cache is KEYED BY TOKEN, which is what makes it safe:
 *   - logout (token gone)      -> next call sees a different key and resets;
 *   - a fresh login (new token) -> next call sees a different key and refetches.
 * A single module-level promise with no key would be poisoned for the rest of
 * the session by one bad answer (notably the pre-login 401 below).
 */
let myModulesPromise = null
let myModulesToken = null
let myModulesBootstrapState = null

/**
 * Drop the cached answer. Exported so an explicit logout can clear it without
 * relying on a full page reload.
 */
export const resetMyModulesCache = () => {
    myModulesPromise = null
    myModulesToken = null
    myModulesBootstrapState = null
    // Remove the old cache created by earlier builds. Module permissions are
    // deliberately never persisted in localStorage or sessionStorage.
    try { window.sessionStorage.removeItem('oatrx.myModules') } catch (error) {}
}

/**
 * REALTIME INVALIDATION.
 *
 * The cache above is keyed by token, which is correct for login/logout but
 * means an OPEN session keeps its very first answer until a full page reload.
 * That is what made a grant/revoke invisible without F5 — and, worse, what made
 * a session that started BEFORE the pharmacy was ever configured sit on
 * `modules: null` (legacy → every tab visible) for the rest of the day, even
 * after an admin had restricted the user.
 *
 * `moduleAccessVersion` is bumped whenever the backend tells us the assignment
 * changed (Socket.IO). Every mounted consumer of
 * useMyModules depends on it, so one bump re-runs the fetch ONCE (the token
 * cache collapses the concurrent calls) and re-renders the nav, the sidebar,
 * the profile dropdown and the route guards together, from this one source.
 */
let moduleAccessVersion = 0
const versionListeners = new Set()
let portalReturnListenerCount = 0
let portalReturnHandler = null

export const refreshMyModules = () => {
    resetMyModulesCache()
    moduleAccessVersion += 1
    // Copied before iterating: a listener may unsubscribe while we notify.
    Array.from(versionListeners).forEach((listener) => {
        try {
            listener(moduleAccessVersion)
        } catch (error) {
            // A single bad consumer must never stop the others refreshing.
        }
    })
}

/**
 * Socket.IO is the fast path for a live Admin change. This is the safe
 * recovery path for a missed socket event (for example, while the browser tab
 * was backgrounded): when the user returns to the Pharmacy Portal, discard the
 * cached answer and ask the authoritative `my-modules` endpoint again.
 *
 * This listener is shared by all useMyModules consumers, so returning to the
 * tab still produces one re-fetch, not one request per menu/route component.
 */
const subscribePortalReturn = () => {
    portalReturnListenerCount += 1

    if (portalReturnListenerCount !== 1 || typeof window === 'undefined' || typeof document === 'undefined') {
        return
    }

    portalReturnHandler = () => {
        // Session-only policy: changing browser focus/visibility must never
        // re-fetch module permissions. They are read once after hard refresh
        // (or after a new login token) and then served from the token cache.
        // if (document.visibilityState === 'visible') refreshMyModules()
    }

    window.addEventListener('focus', portalReturnHandler)
    document.addEventListener('visibilitychange', portalReturnHandler)
}

const unsubscribePortalReturn = () => {
    portalReturnListenerCount -= 1

    if (portalReturnListenerCount > 0 || !portalReturnHandler || typeof window === 'undefined' || typeof document === 'undefined') {
        return
    }

    window.removeEventListener('focus', portalReturnHandler)
    document.removeEventListener('visibilitychange', portalReturnHandler)
    portalReturnHandler = null
    portalReturnListenerCount = 0
}

const fetchMyModules = () => {
    const token = authToken()

    // Remove permission data written by older builds. This hook keeps its
    // result only in memory for the current app instance.
    try { window.sessionStorage.removeItem('oatrx.myModules') } catch (error) {}

    // PRE-LOGIN: never fire this request without a token. It would 401, and the
    // response interceptor turns ANY 401 into "clear every cookie, purge the
    // store, redirect to /" — which on the login page means a wiped session and
    // a bounced/looping login screen. No token is not an error condition: it is
    // simply "not logged in yet", which is legacy mode (modules: null), exactly
    // the fail-open contract above.
    if (!token) {
        resetMyModulesCache()
        return Promise.resolve(LEGACY_STATE)
    }

    if (myModulesPromise && myModulesToken === token) return myModulesPromise

    myModulesToken = token
    myModulesPromise = Axios.get(`${path.pharmacy_user_my_modules}`, {
        // The access list must always be resolved from the backend after a
        // hard refresh. Do not let an intermediary reuse an earlier answer.
        headers: { 'Cache-Control': 'no-cache' },
    })
        .then((response) => {
            // Contract shape is the bare object; tolerate a { data: {...} } wrapper too.
            const body = response?.data
            const data = (body && Array.isArray(body.modules)) ? body : body?.data
            // An EMPTY list splits on the backend's explicit marker (product
            // decision: "the user with no module should not be able to see
            // anything"):
            //   `legacy: false` alongside `modules: []` — the NEW backend
            //     asserting "this emptiness is a real permission answer" (the
            //     pharmacy is admin-configured and this user's effective set is
            //     genuinely empty). Gate closed: every tab, sidebar item and
            //     route is hidden, and `emptyGate` lets the dashboard show its
            //     "no modules assigned" notice instead of a blank void.
            //   anything else — an old backend (or unknown shape) that produced
            //     `[]` without the marker. That is still always a data gap, and
            //     an empty gate is the one failure mode that locks a pharmacist
            //     out mid-shift: fail OPEN, exactly like a 404 or a 500.
            if (data && Array.isArray(data.modules) && data.modules.length === 0) {
                if (data.legacy === false) {
                    return {
                        modules: new Set(),
                        parents: new Set(),
                        isAdmin: data.is_admin === true,
                        mode: data.mode ?? null,
                        emptyGate: true,
                        loading: false,
                    }
                }
                return LEGACY_STATE
            }
            if (data && Array.isArray(data.modules)) {
                return {
                    modules: new Set(data.modules),
                    // Absent on a backend from before sub-modules → null, and
                    // callers fall back to the `modules` check.
                    parents: Array.isArray(data.parents) ? new Set(data.parents) : null,
                    isAdmin: data.is_admin === true,
                    mode: data.mode ?? null,
                    emptyGate: false,
                    loading: false,
                }
            }
            // Missing/unknown shape → legacy mode.
            return LEGACY_STATE
        })
        .catch(() => LEGACY_STATE)

    return myModulesPromise
}

/**
 * Resolve the current user's module access before React mounts on a hard
 * refresh. This is runtime-only state: it is never written to browser
 * storage, cookies, or an HTTP cache. Starting with this value prevents the
 * workspace bar from first rendering either all modules or an empty bar.
 */
export const primeMyModules = () => fetchMyModules().then((result) => {
    myModulesBootstrapState = result
    return result
})

const useMyModules = () => {
    // Read on every render so the effect below re-runs the moment the token
    // appears (login) or disappears (logout). AppRoutes stays mounted across
    // login, so a mount-only effect would leave the gate stuck on the
    // pre-login answer for the whole session.
    const token = authToken()
    const [state, setState] = useState(() => myModulesBootstrapState
        ? myModulesBootstrapState
        : { ...LEGACY_STATE, loading: true })
    const [version, setVersion] = useState(moduleAccessVersion)
    const mountedRef = useRef(true)
    const stateTokenRef = useRef(token)

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // Re-run the fetch below whenever refreshMyModules() bumps the version.
    // Subscribing on mount (not on every render) and reading the CURRENT
    // version immediately closes the gap where a bump lands between render and
    // effect — otherwise that consumer would keep the pre-grant answer.
    useEffect(() => {
        const listener = (next) => setVersion(next)
        versionListeners.add(listener)
        setVersion(moduleAccessVersion)
        return () => { versionListeners.delete(listener) }
    }, [])

    // A changed Admin setting normally arrives through `module_access_updated`.
    // Revalidate once on return as well, so a missed realtime event cannot keep
    // an old legacy permission (such as pharmacy_daily_dispense) visible.
    useEffect(() => {
        subscribePortalReturn()
        return unsubscribePortalReturn
    }, [])

    useEffect(() => {
        let active = true
        // AppRoutes remains mounted across logout/login. Clear the displayed
        // gate while the new identity's authoritative request is in flight.
        if (stateTokenRef.current !== token) {
            stateTokenRef.current = token
            setState({ ...LEGACY_STATE, loading: true })
        }
        fetchMyModules().then((result) => {
            myModulesBootstrapState = result
            // `active` guards against a stale in-flight answer from a previous
            // token overwriting the current one.
            if (mountedRef.current && active) setState(result)
        })
        return () => { active = false }
    }, [token, version])

    return state
}

/**
 * The two checks every consumer needs, in one place.
 *
 *   hasKey(key)      — does the user hold this module/sub-module key?
 *   tabAllowed(name) — should this top-nav tab render? With sub-modules the tab
 *                      is DERIVED: it shows when the user holds at least one of
 *                      its sub-modules, which is what `parents` says. Against a
 *                      backend from before sub-modules there is no `parents`,
 *                      so it falls back to the top-level key in `modules` —
 *                      today's exact behaviour.
 *
 * Both return true in legacy mode (modules === null), so the legacy permission
 * logic remains the sole authority whenever the backend cannot answer.
 */
export const buildModuleGate = ({ modules, parents, loading = false }) => {
    // Render while the authoritative response is pending. Blocking this gate
    // makes the workspace bar disappear and, together with a route guard,
    // leaves a completely blank page on hard reload. Backend authorization
    // remains the security boundary; once the response arrives, its module
    // assignments immediately replace this temporary legacy-compatible view.
    if (loading) {
        return {
            legacy: true,
            hasKey: () => true,
            tabAllowed: () => true,
        }
    }

    const legacy = modules === null

    const hasKey = (key) => legacy || modules.has(key)

    const tabAllowed = (tabName) => {
        if (legacy) return true
        const moduleKey = WORKSPACE_TAB_MODULE_KEYS[tabName]
        if (moduleKey === undefined) return true // tabs outside the module contract
        return parents ? parents.has(moduleKey) : modules.has(moduleKey)
    }

    return { legacy, hasKey, tabAllowed }
}

export default useMyModules
